import React, { useState, useEffect, useRef } from "react";
import * as d3 from "d3";
import { PopupData, colors } from "../types/data";
import { BinData, PopupStatistic } from "../types/data";

// Back end will return the counts and stuff

interface Interval {
  low: number;
  high: number;
}

interface Props {
  popupData: PopupData;
  numberOfIntervals: number;
  currentValue: number;
  handleValue: (val: number) => void;
}

const findInterval = (number: number, intervals: Interval[]): number => {
  for (let i = 0; i < intervals.length; i++) {
    if (number >= intervals[i].low && number < intervals[i].high) {
      return i + 1;
    }
  }
  if (number >= intervals[intervals.length - 1].high) {
    return intervals.length - 1;
  }

  return -1; // Number does not lie in any interval
};

const mapValue = (
  value: number,
  min: number,
  max: number,
  numberOfIntervals: number
): number => {
  // Calculate the mapped value to a new range
  const range = max - min;
  const newRange = numberOfIntervals;
  const scaledValue = (value - min) / range;
  return scaledValue * newRange;
};

const createBins = (
  data: number[],
  intervals: Interval[],
  min: number,
  max: number
): BinData[] => {
  data.sort();

  const intervalCount: number[] = Array(intervals.length).fill(0);

  for (let i = 0; i < data.length; i++) {
    const actual = mapValue(data[i], min, max, intervals.length);
    for (let j = 0; j < intervals.length; j++) {
      if (actual >= intervals[j].low && actual < intervals[j].high) {
        intervalCount[j]++;
      }
    }
  }

  const newData: BinData[] = [];

  for (let i = 0; i < intervals.length; i++) {
    newData.push({
      start: intervals[i].low,
      end: intervals[i].high,
      count: Math.round((intervalCount[i] * 100) / data.length),
    });
  }

  return newData;
};

const Histogram: React.FC<Props> = ({
  popupData,
  numberOfIntervals,
  currentValue,
  handleValue,
}) => {
  const svgRef = useRef(null);

  /// Specifying the chart dimensions.
  const barHeight = 25;
  const marginTop = 30;
  const marginRight = 60;
  const marginBottom = 10;
  const marginLeft = 60;
  const height = Math.ceil((10 + 0.2) * barHeight) + marginTop + marginBottom;
  const width = 700;

  // Creating scales
  const generateAxis = () => {
    const val = [];
    for (let i = 0; i < numberOfIntervals; i++) {
      val.push({ value: i + 1 });
    }
    return val;
  };

  const data: { value: number }[] = generateAxis();

  const y = d3
    .scaleLinear()
    .domain([numberOfIntervals, 1])
    .rangeRound([marginTop, height - marginBottom])
    .nice();

  const x = d3
    .scaleLinear()
    .domain([-100, 100])
    .rangeRound([marginLeft, width - marginRight]);

  // Define a function to divide an interval into equal pieces
  const intervals: Interval[] = [];
  const max = Math.max(popupData.league.max, popupData.team.max);
  const min = Math.min(popupData.league.min, popupData.team.min);

  const intervalSize = 1;
  for (let i = 0; i < numberOfIntervals; i++) {
    intervals.push({
      low: i * intervalSize,
      high: (i + 1) * intervalSize,
    });
  }

  const league_data = createBins(popupData.league.data, intervals, min, max);
  const team_data = createBins(popupData.team.data, intervals, min, max);

  // Defining colors for the heatmap
  const legendColors = d3
    .scaleSequential(d3.interpolateRgbBasis(["red", "yellow", "green"]))
    .domain([1, 10]);

  useEffect(() => {
    const svg = d3.select(svgRef.current);

    // Bars for the league.
    svg
      .append("g")
      .selectAll()
      .data(league_data)
      .join("rect")
      .attr("fill", colors[0])
      .attr("rx", 5)
      .attr("x", (d) => x(-1 * d.count))
      .attr("y", (d) => y(findInterval(d.start, intervals)) - 25)
      .attr("width", (d) => Math.abs(x(-1 * d.count) - x(0)))
      .attr("height", barHeight);

    // Bars for the team.
    svg
      .append("g")
      .selectAll()
      .data(team_data)
      .join("rect")
      .attr("rx", 5)
      .attr("fill", colors[1])
      .attr("x", (d) => x(Math.min(0, d.count)))
      .attr("y", (d) => y(findInterval(d.start, intervals)) - barHeight)
      .attr("width", (d) => Math.abs(x(d.count) - x(0)))
      .attr("height", barHeight);

    // Heatmap in the middle.
    svg
      .append("g")
      .selectAll("rect")
      .data(data)
      .enter()
      .append("rect")
      .attr("x", x(0) - 5)
      .attr("y", (d, i) => y(d.value) - barHeight)
      .attr("width", 10)
      .attr("height", barHeight + 5)
      .attr("fill", (d) => legendColors(d.value));

    // Drawing the lines for the mean
    const lineGroup = svg.append("g");

    // Append line
    lineGroup
      .append("line")
      .attr("class", "league_mean")
      .attr("x1", marginLeft + 20) // Starting x-coordinate
      .attr(
        "y1",
        y(
          findInterval(
            mapValue(popupData.league.mean, min, max, numberOfIntervals),
            intervals
          )
        )
      ) // Starting y-coordinate
      .attr("x2", x(0) - 5) // Ending x-coordinate
      .attr(
        "y2",
        y(
          findInterval(
            mapValue(popupData.league.mean, min, max, numberOfIntervals),
            intervals
          )
        )
      ) // Ending y-coordinate
      .attr("stroke", colors[0])
      .attr("stroke-width", 2);

    lineGroup
      .append("line")
      .attr("class", "team_mean")
      .attr("x1", x(0) + 5) // Starting x-coordinate
      .attr(
        "y1",
        y(
          findInterval(
            mapValue(popupData.team.mean, min, max, numberOfIntervals),
            intervals
          )
        )
      ) // Starting y-coordinate
      .attr("x2", x(0) + width / 2 - marginRight) // Ending x-coordinate
      .attr(
        "y2",
        y(
          findInterval(
            mapValue(popupData.team.mean, min, max, numberOfIntervals),
            intervals
          )
        )
      ) // Ending y-coordinate
      .attr("stroke", colors[1])
      .attr("stroke-width", 2);

    lineGroup
      .append("text")
      .attr("x", x(0) + width / 2 - marginRight - 25) // Text offset from the end of the line
      .attr(
        "y",
        y(
          findInterval(
            mapValue(popupData.team.mean, min, max, numberOfIntervals),
            intervals
          )
        ) + 15
      ) // Text y-coordinate (same as line)
      .attr("alignment-baseline", "middle")
      .text(`Mean: ${popupData.team.mean.toFixed(2)}`);

    lineGroup
      .append("text")
      .attr("x", marginLeft + 25) // Text offset from the end of the line
      .attr(
        "y",
        y(
          findInterval(
            mapValue(popupData.league.mean, min, max, numberOfIntervals),
            intervals
          )
        ) /
          2 +
          25
      ) // Text y-coordinate (same as line)
      .attr("alignment-baseline", "middle")
      .text(`Mean: ${popupData.league.mean.toFixed(2)}`);

    // The slider
    const sliderButton = svg.append("g");
    const sliderHandle = sliderButton
      .append("circle")
      .attr("cx", x(0))
      .attr(
        "cy",
        y(
          findInterval(
            mapValue(
              Number(currentValue.toFixed(2)),
              min,
              max,
              numberOfIntervals
            ),
            intervals
          )
        )
      )
      .attr("r", 25)
      .attr("fill", "white")
      .attr("stroke", "gray")
      .attr("cursor", "grab");

    const text = sliderButton
      .append("text")
      .attr("class", "circle_value")
      .attr("x", x(0))
      .attr(
        "y",
        y(
          findInterval(
            mapValue(
              Number(currentValue.toFixed(2)),
              min,
              max,
              numberOfIntervals
            ),
            intervals
          )
        )
      )
      .attr("text-anchor", "middle")
      .attr("cursor", "grab")
      .attr("alignment-baseline", "middle")
      .text(`${currentValue.toFixed(2)}`);

    // Dragging behavior for slider handle circle
    const dragSlider = d3.drag<SVGGElement, any>().on("drag", function (event) {
      const yPos = event.y;
      // Constrain circle within track
      const yMin = marginTop;
      const yMax = height - marginBottom - 25;
      const constrainedY = Math.min(Math.max(yMin, yPos), yMax);
      sliderHandle.attr("cy", constrainedY);
      const value = y.invert(yPos).toFixed(2);
      const mapping = mapValue(Number(value), 0, 10, max - min);
      handleValue(mapping);
      const result: number =
        mapping < min ? min : mapping > max ? max : mapping;
      text.attr("y", constrainedY).text(`${result.toFixed(2)}`);
    });

    sliderButton.call(dragSlider);
  }, []);

  return <svg ref={svgRef} width={700} height={height + 25}></svg>;
};

export default Histogram;
